export type PropertyStatusKey =
    | 'available'
    | 'reserved'
    | 'reservedwithdeposit'
    | 'contracted'
    | 'sold'
    | 'draft'
    | 'pendingapproval'
    | 'unpublished'
    | 'unknown';

export type PropertyStatusOption = {
    value: string;
    label: string;
};

export const PROPERTY_STATUS_EDIT_OPTIONS: PropertyStatusOption[] = [
    { value: 'Available', label: 'Available' },
    { value: 'ReservedOrHold', label: 'Reserved / Hold' },
    { value: 'ReservedWithDeposit', label: 'Reserved with deposit' },
    { value: 'Sold', label: 'Sold' },
    { value: 'Contracted', label: 'Contracted' }
];

export const PROPERTY_STATUS_FORM_OPTIONS: PropertyStatusOption[] = [
    ...PROPERTY_STATUS_EDIT_OPTIONS,
    { value: 'PendingApproval', label: 'Pending Approval' },
    { value: 'Unpublished', label: 'Unpublished' }
];

export function normalizePropertyStatusKey(status: unknown): string {
    return String(status || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');
}

export function propertyStatusKey(status: unknown): PropertyStatusKey {
    const key = normalizePropertyStatusKey(status);

    if (['available', 'active', 'published', 'publish', 'open'].includes(key)) return 'available';
    if (['reserved', 'booking', 'booked', 'hold', 'held', 'reservedhold', 'reservedorhold'].includes(key)) return 'reserved';
    if (['reservedwithdeposit', 'reserveddeposit', 'depositreserved'].includes(key)) return 'reservedwithdeposit';
    if (['contracted', 'contract'].includes(key)) return 'contracted';
    if (['sold', 'closed', 'completed'].includes(key)) return 'sold';
    if (['draft', 'pending'].includes(key)) return 'draft';
    if (['pendingapproval', 'approvalpending'].includes(key)) return 'pendingapproval';
    if (['unpublished', 'inactive', 'archived'].includes(key)) return 'unpublished';
    return 'unknown';
}

export function propertyStatusLabel(status: unknown): string {
    switch (propertyStatusKey(status)) {
        case 'available':
            return 'Available';
        case 'reserved':
            return 'Reserved / Hold';
        case 'reservedwithdeposit':
            return 'Reserved with deposit';
        case 'contracted':
            return 'Contracted';
        case 'sold':
            return 'Sold';
        case 'draft':
            // Truthful label. This function is shared with the super-admin
            // approval console, where "Draft" is the whole point of the screen.
            // Hiding the draft badge from the people working the inventory is a
            // RENDERING decision — use shouldShowPropertyStatusBadge() for that,
            // not an empty label, which only produced empty pill shells.
            return 'Draft';
        case 'pendingapproval':
            return 'Pending Approval';
        case 'unpublished':
            return 'Unpublished';
        default: {
            const raw = String(status || '').trim();
            return raw || 'Status Unknown';
        }
    }
}

/**
 * Tailwind classes for a unit-status pill, shared by every surface that
 * renders one (lead profile header, the lead dialogs' unit picker). Keyed off
 * propertyStatusKey so any stored spelling resolves to the same colour.
 */
export function propertyStatusBadgeClass(status: unknown): string {
    switch (propertyStatusKey(status)) {
        case 'available':
            return 'bg-emerald-50 text-emerald-700 border-emerald-200';
        case 'reserved':
            return 'bg-amber-50 text-amber-800 border-amber-200';
        case 'reservedwithdeposit':
            return 'bg-orange-50 text-orange-800 border-orange-200';
        case 'contracted':
            return 'bg-rose-50 text-rose-700 border-rose-200';
        case 'sold':
            return 'bg-red-50 text-red-700 border-red-200';
        default:
            return 'bg-slate-50 text-slate-700 border-slate-200';
    }
}

/**
 * Should a unit's status pill be rendered at all on an INVOLVED-USER surface
 * (developer / partner / sales / investor unit views)?
 *
 * Client request (item 5): "Draft/unpublished units should be visible to the
 * users involved (without a draft badge) in the project the units belong to."
 * `properties.status` is a single column that mixes sale status
 * (available/reserved/sold/contracted) with lifecycle state (draft), so a draft
 * unit has no sale status to show — the honest rendering is no pill, not an
 * empty one.
 *
 * Lifecycle badges are operational metadata for platform admins. Everyone else
 * still sees the unit, but without Draft / Unpublished / Pending Approval pills.
 * Admin approval surfaces deliberately do not call this helper, so their queues
 * keep the lifecycle labels they need.
 */
export function shouldShowPropertyStatusBadge(status: unknown, viewerIsAppAdmin = false): boolean {
    const key = propertyStatusKey(status);
    if (viewerIsAppAdmin) return true;
    return !['draft', 'unpublished', 'pendingapproval'].includes(key);
}

/**
 * Map a status value of ANY casing/spelling to the canonical edit-option value
 * (`Available`, `ReservedOrHold`, `ReservedWithDeposit`, `Sold`, `Contracted`,
 * `Draft`, `PendingApproval`, `Unpublished`). The API returns lowercase status
 * codes (e.g. `"available"`), but the status <select> options use these
 * capitalized values — binding the raw lowercase value leaves the select empty
 * because it never matches an option. Normalize through the status key so the
 * select shows the right option regardless of source casing.
 */
export function propertyStatusEditValue(status: unknown): string {
    switch (propertyStatusKey(status)) {
        case 'available':
            return 'Available';
        case 'reserved':
            return 'ReservedOrHold';
        case 'reservedwithdeposit':
            return 'ReservedWithDeposit';
        case 'contracted':
            return 'Contracted';
        case 'sold':
            return 'Sold';
        case 'draft':
            return 'Draft';
        case 'pendingapproval':
            return 'PendingApproval';
        case 'unpublished':
            return 'Unpublished';
        default:
            return 'Available';
    }
}
